/**
 * The bill of materials (BOM) for the editor's panels, recomputed as panels change. Same rules as the API
 * (apps/solar-backend/app/design/editor/bom.py, `GET /designs/{id}/bom`), which also gives the equipment and unit
 * prices (`context`). Keep the two in step.
 *
 * Racking (an ordering estimate, not an engineered plan):
 * - rows: panels on one face with the same orientation and rotation whose centres are within half a panel up the
 *   slope; a row breaks where neighbours are more than one panel plus `row_break_m` apart;
 * - two rails per row, row length plus `rail_overhang_m` at each end, cut from `rail_stick_m` sticks (splices join
 *   them); one attachment every `attachment_spacing_m` along each rail plus one (at least two);
 * - two mid clamps between neighbours, four end clamps per row.
 * Pure (no React), unit tested with node:test.
 *
 * The lines keep the API's English words (`item`, `note`: the parts order sends them, bom.py writes the same); what a
 * person reads comes from `labels` (catalog keys and values, one per line) through `bomText(t, …)`.
 */
import type { S } from "../api/client";
import type { Translate } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";

export type BomContext = S["BomContext"];
export type BomLine = S["BomLine"];

export interface Placed {
  face_id: string;
  orientation: "portrait" | "landscape" | string;
  /** Centre in the face's uv frame (metres). */
  center: [number, number];
  rotation: number;
}

export interface Racking {
  rows: number;
  rail_m: number;
  rail_pieces: number;
  splices: number;
  attachments: number;
  mid_clamps: number;
  end_clamps: number;
}

type Module = Pick<S["BomModule"], "width_m" | "height_m">;
type Rules = S["BomRackingRules"];

/** [along the rail, up the slope] of one panel. */
function dims(orientation: string, module: Module): [number, number] {
  return orientation === "portrait" ? [module.width_m, module.height_m] : [module.height_m, module.width_m];
}

/** Each row as the positions of its panels along the rail (metres, ascending). */
export function panelRows(panels: Placed[], module: Module, rules: Rules): number[][] {
  const groups = new Map<string, Placed[]>();
  for (const p of panels) {
    const key = `${p.face_id}\u0000${p.orientation}\u0000${Math.round(p.rotation)}`;
    groups.set(key, [...(groups.get(key) ?? []), p]);
  }
  const rows: number[][] = [];
  for (const key of [...groups.keys()].sort()) {
    const members = groups.get(key)!;
    const [across, upSize] = dims(members[0]!.orientation, module);
    const angle = (Math.round(members[0]!.rotation) * Math.PI) / 180;
    const ax = Math.cos(angle);
    const ay = Math.sin(angle);
    const placed = members
      .map((p) => [p.center[0] * ax + p.center[1] * ay, -p.center[0] * ay + p.center[1] * ax] as [number, number])
      .sort((a, b) => a[1] - b[1] || a[0] - b[0]);
    const clusters: [number, number][][] = [];
    for (const pt of placed) {
      const last = clusters[clusters.length - 1];
      if (last && Math.abs(pt[1] - last[last.length - 1]![1]) <= upSize / 2) last.push(pt);
      else clusters.push([pt]);
    }
    for (const cluster of clusters) {
      const positions = cluster.map((c) => c[0]).sort((a, b) => a - b);
      let run = [positions[0]!];
      for (const a of positions.slice(1)) {
        if (a - run[run.length - 1]! > across + rules.row_break_m) {
          rows.push(run);
          run = [];
        }
        run.push(a);
      }
      rows.push(run);
    }
  }
  return rows;
}

export function racking(panels: Placed[], module: Module, rules: Rules): Racking {
  const t: Racking = { rows: 0, rail_m: 0, rail_pieces: 0, splices: 0, attachments: 0, mid_clamps: 0, end_clamps: 0 };
  for (const orientation of [...new Set(panels.map((p) => p.orientation))].sort()) {
    const [across] = dims(orientation, module);
    for (const row of panelRows(panels.filter((p) => p.orientation === orientation), module, rules)) {
      const length = row[row.length - 1]! - row[0]! + across + 2 * rules.rail_overhang_m;
      const pieces = Math.ceil(length / rules.rail_stick_m - 1e-9);
      t.rows += 1;
      t.rail_m += 2 * length;
      t.rail_pieces += 2 * pieces;
      t.splices += 2 * (pieces - 1);
      t.attachments += 2 * Math.max(2, Math.ceil(length / rules.attachment_spacing_m - 1e-9) + 1);
      t.mid_clamps += 2 * (row.length - 1);
      t.end_clamps += 4;
    }
  }
  t.rail_m = Math.round(t.rail_m * 100) / 100;
  return t;
}

export function inverterCount(panels: number, moduleWatts: number, inverter: S["BomInverter"]): number {
  if (panels === 0) return 0;
  const count =
    inverter.type === "micro"
      ? Math.ceil(panels / inverter.modules_per_unit)
      : Math.ceil((panels * moduleWatts) / 1000 / inverter.dc_ac_ratio_max / inverter.rated_ac_kw - 1e-9);
  return inverter.round_to_even && count % 2 ? count + 1 : count;
}

const fmt = (v: number) => String(Math.round(v * 1000) / 1000);
const round3 = (v: number) => Math.round(v * 1000) / 1000;

/** A text in the reader's language: a catalog key and its values. */
export interface BomText {
  key: MessageKey;
  values?: Record<string, string | number>;
}
/** What a person reads of one line: its item (a key, or data such as the battery's own name) and note. */
export interface BomLabel {
  item: BomText | string;
  note: BomText | null;
}

export function bomText(t: Translate, text: BomText | string | null | undefined): string | null {
  if (text == null) return null;
  return typeof text === "string" ? text : t(text.key, text.values);
}

function line(category: BomLine["category"], item: string, quantity: number, opts: { model?: string | null; unit_price?: number | null; note?: string | null; unit?: string } = {}): BomLine {
  const price = opts.unit_price ?? null;
  return {
    category,
    item,
    model: opts.model ?? null,
    quantity,
    unit: opts.unit ?? "pcs",
    unit_price: price,
    total: price != null ? Math.round(price * quantity * 100) / 100 : null,
    note: opts.note ?? null,
  };
}

/** The bill of materials for `panels` with the design's equipment (`context` from the API). */
export function bomLines(panels: Placed[], context: BomContext): { lines: BomLine[]; labels: BomLabel[]; racking: Racking } {
  const n = panels.length;
  const { module, inverter } = context;
  const lines: BomLine[] = [];
  const labels: BomLabel[] = [];
  const add = (l: BomLine, item: BomText | string, note: BomText | null = null) => {
    lines.push(l);
    labels.push({ item, note });
  };
  add(
    line("panels", "Solar panel", n, { model: module.model, unit_price: module.unit_price, note: `${fmt(module.watts)} W, ${module.width_m.toFixed(3)} × ${module.height_m.toFixed(3)} m` }),
    { key: "editor.bom.item.panel" },
    { key: "editor.bom.note.panel", values: { watts: round3(module.watts), width: module.width_m, height: module.height_m } },
  );
  const count = inverterCount(n, module.watts, inverter);
  if (count) {
    const micro = inverter.type === "micro";
    add(
      line("inverters", micro ? "Microinverter" : "String inverter", count, { model: inverter.model, unit_price: inverter.unit_price, note: micro ? `${inverter.modules_per_unit} panel(s) per unit` : `${fmt(inverter.rated_ac_kw)} kW AC` }),
      { key: micro ? "editor.bom.item.microinverter" : "editor.bom.item.stringInverter" },
      micro ? { key: "editor.bom.note.panelsPerUnit", values: { count: inverter.modules_per_unit } } : { key: "editor.bom.note.acPower", values: { kw: round3(inverter.rated_ac_kw) } },
    );
  }
  if (context.optimizer && n && inverter.type === "string") {
    add(line("optimizers", "Panel optimiser", Math.ceil(n / context.optimizer.modules_per_unit), { model: context.optimizer.model, unit_price: context.optimizer.unit_price }), { key: "editor.bom.item.optimizer" });
  }
  if (context.battery) {
    const b = context.battery;
    const parts = [b.power_modules ? `${b.power_modules} power module(s)` : null, b.battery_modules ? `${b.battery_modules} battery module(s)` : null].filter(Boolean);
    const note: BomText | null =
      b.power_modules && b.battery_modules
        ? { key: "editor.bom.note.powerAndBatteryModules", values: { power: b.power_modules, battery: b.battery_modules } }
        : b.power_modules
          ? { key: "editor.bom.note.powerModules", values: { count: b.power_modules } }
          : b.battery_modules
            ? { key: "editor.bom.note.batteryModules", values: { count: b.battery_modules } }
            : null;
    add(line("battery", b.label, 1, { model: b.model, unit_price: b.price, note: parts.join(", ") || null }), b.label, note);
  }
  const rack = racking(panels, module, context.racking);
  if (n) {
    add(
      line("racking", `Rail, ${fmt(context.racking.rail_stick_m)} m`, rack.rail_pieces, { note: `${rack.rows} row(s), ${fmt(rack.rail_m)} m of rail` }),
      { key: "editor.bom.item.rail", values: { length: round3(context.racking.rail_stick_m) } },
      { key: "editor.bom.note.rail", values: { rows: rack.rows, length: round3(rack.rail_m) } },
    );
    add(line("racking", "Rail splice", rack.splices), { key: "editor.bom.item.splice" });
    add(
      line("racking", "Roof attachment", rack.attachments, { note: `every ${fmt(context.racking.attachment_spacing_m)} m along each rail` }),
      { key: "editor.bom.item.attachment" },
      { key: "editor.bom.note.attachment", values: { spacing: round3(context.racking.attachment_spacing_m) } },
    );
    add(line("racking", "Mid clamp", rack.mid_clamps), { key: "editor.bom.item.midClamp" });
    add(line("racking", "End clamp", rack.end_clamps), { key: "editor.bom.item.endClamp" });
  }
  return { lines, labels, racking: rack };
}

export function bomTotal(lines: BomLine[]): number | null {
  const priced = lines.filter((l) => l.total != null);
  return priced.length ? Math.round(priced.reduce((s, l) => s + (l.total ?? 0), 0) * 100) / 100 : null;
}

const CATEGORY: Record<BomLine["category"], string> = {
  panels: "Panels",
  inverters: "Inverters",
  optimizers: "Optimisers",
  battery: "Battery",
  racking: "Racking",
};
const CATEGORY_KEY: Record<BomLine["category"], MessageKey> = {
  panels: "editor.bom.category.panels",
  inverters: "editor.bom.category.inverters",
  optimizers: "editor.bom.category.optimizers",
  battery: "editor.bom.category.battery",
  racking: "editor.bom.category.racking",
};
export const categoryLabel = (t: Translate, c: BomLine["category"]) => t(CATEGORY_KEY[c]);

function csvCell(value: string | number | null | undefined): string {
  if (value == null) return "";
  const text = String(value);
  // quote when needed; a leading = + - @ is prefixed so a spreadsheet never runs it as a formula
  const safe = /^[=+\-@\t\r]/.test(text) && typeof value === "string" ? `'${text}` : text;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** The list as CSV (one header row; prices in `currency`, blank when unknown). With `words` (the translator and the
 * lines' labels) the headers and texts are in the reader's language; without, the API's English. Numbers stay plain
 * (no grouping) so a spreadsheet reads them in any language. */
export function bomCsv(lines: BomLine[], currency: string, words?: { t: Translate; labels: BomLabel[] }): string {
  const t = words?.t;
  const head = t
    ? [t("editor.bom.csv.category"), t("editor.bom.csv.item"), t("editor.bom.csv.model"), t("editor.bom.csv.quantity"), t("editor.bom.csv.unit"), t("editor.bom.csv.unitPrice", { currency }), t("editor.bom.csv.total", { currency }), t("editor.bom.csv.note")]
    : ["Category", "Item", "Model", "Quantity", "Unit", `Unit price (${currency})`, `Total (${currency})`, "Note"];
  const rows = lines.map((l, i) => {
    const label = words?.labels[i];
    return t
      ? [categoryLabel(t, l.category), label ? bomText(t, label.item) : l.item, l.model, l.quantity, l.unit === "pcs" ? t("editor.bom.unit.pieces") : l.unit, l.unit_price, l.total, label ? bomText(t, label.note) : l.note]
      : [CATEGORY[l.category], l.item, l.model, l.quantity, l.unit, l.unit_price, l.total, l.note];
  });
  const total = bomTotal(lines);
  if (total != null) rows.push(["", t ? t("editor.bom.csv.pricedTotal") : "Total (priced lines)", "", "", "", "", total, ""]);
  return [head, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

/** "12 Oak St., Springfield" → "12-oak-st-springfield": lower case ASCII letters, digits and dashes, at most 60. */
export function slugify(text: string): string {
  return text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 60)
    .replace(/^-+|-+$/g, "");
}

/** The CSV's file name: `bill-of-materials-<address>-v<version>.csv` (the design's name when it has no address). */
export function bomFilename(address: string | null | undefined, designName: string | null | undefined, version: number): string {
  const place = slugify(address || "") || slugify(designName || "") || "design";
  return `bill-of-materials-${place}-v${version}.csv`;
}
